import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Copy,
  FileText,
  Plus,
  Save,
  Send,
  Trash2,
  UploadCloud
} from 'lucide-react';
import {
  EventOption,
  OutlookIssueItem,
  OutlookMeetingFeedback,
  OutlookMeetingRecord,
  OutlookReportGroup,
  OutlookRecordStatus
} from '../types';
import {
  getOutlookRecords,
  saveOutlookRecord
} from '../services/firebaseService';
import { ToastType } from './Toast';
import { sanitizeText } from '../utils/sanitize';

const DEFAULT_DEPARTMENTS = ['內科', '中醫', '物理治療', '牙科', '心電圖', '檢驗', '義剪', '心靈關懷科'];
const DEFAULT_REPORT_GROUPS = [
  '掛號',
  '內科',
  '中醫',
  '物理治療',
  '牙科',
  '器消',
  '藥局',
  '義剪',
  '資訊',
  '福音組',
  '小小醫師營',
  '心靈關懷科',
  '心電圖',
  '檢驗',
];
const DEFAULT_MEETING_FEEDBACKS = [
  '宣道處傳道',
  '地方教會',
  '隊長/副隊長',
];

const STATUS_LABEL: Record<OutlookRecordStatus, string> = {
  draft: '草稿',
  review: '待審核',
  published: '已發布',
};

const STATUS_CLASS: Record<OutlookRecordStatus, string> = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  review: 'bg-amber-100 text-amber-800 border-amber-200',
  published: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const makeId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const formatCount = (value: number): string => value.toLocaleString('zh-TW');
const parseStatCount = (value: string): number => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const makeDefaultDayStats = (): OutlookMeetingRecord['dayStats'] => [
  { id: makeId('day'), label: 'DAY1', patientCount: 0, visitCount: 0 },
  { id: makeId('day'), label: 'DAY2', patientCount: 0, visitCount: 0 },
];

const makeDefaultDepartmentStats = (): OutlookMeetingRecord['departmentStats'] =>
  DEFAULT_DEPARTMENTS.map(name => ({ id: makeId('dept'), name, visitCount: 0 }));

const makeDefaultIssueItem = (): OutlookIssueItem => ({
  id: makeId('issue'),
  issue: '',
  improvementMethod: '',
  owner: '',
  status: 'open',
  carryForward: true,
});

const makeDefaultReportGroups = (): OutlookReportGroup[] =>
  DEFAULT_REPORT_GROUPS.map((name, index) => ({
    id: makeId('group'),
    name,
    order: index + 1,
    isActive: true,
    currentSummary: '',
    items: [makeDefaultIssueItem()],
  }));

const makeDefaultMeetingFeedbacks = (): OutlookMeetingFeedback[] =>
  DEFAULT_MEETING_FEEDBACKS.map((category, index) => ({
    id: makeId('feedback'),
    category,
    speaker: '',
    summary: '',
    followUp: '',
    owner: '',
    order: index + 1,
  }));

const makeEmptyRecord = (event: EventOption, year: number): OutlookMeetingRecord => ({
  id: event.id,
  eventId: event.id,
  year,
  eventDateRange: event.dateRange,
  eventLocation: event.location,
  title: `${year} ${event.dateRange} ${event.location}醫宣義診展望會紀錄`,
  meetingDateTime: '',
  meetingLocation: event.location,
  recorderName: '',
  recorderEmails: [],
  infoTeamName: '',
  status: 'draft',
  publicSummary: '',
  groupReports: '',
  reportGroups: makeDefaultReportGroups(),
  meetingFeedbacks: makeDefaultMeetingFeedbacks(),
  gospelFollowUp: '',
  gratitudeNotes: '',
  dayStats: makeDefaultDayStats(),
  departmentStats: makeDefaultDepartmentStats(),
  genderRatio: '',
  ageRange: '',
  faithDistribution: '',
  referralSourceDistribution: '',
  actionItems: [],
});

const cleanText = (value: string) => sanitizeText(value).trim();

const normalizeEmailList = (value: string): string[] =>
  value
    .split(/[\n,;，；\s]+/)
    .map(email => email.trim().toLowerCase())
    .filter(Boolean)
    .filter((email, index, all) => all.indexOf(email) === index);

const normalizeIssueStatus = (status?: string): OutlookIssueItem['status'] => {
  if (status === 'resolved' || status === 'dropped') return status;
  if (status === 'done') return 'resolved';
  return 'open';
};

const normalizeFeedbackCategory = (category?: string, fallback = ''): string => {
  const value = category || fallback;
  if (value.includes('宣道處')) return '宣道處傳道';
  if (value.includes('地方教會')) return '地方教會';
  if (value.includes('隊長')) return '隊長/副隊長';
  return value;
};

const normalizeIssueItem = (
  item: Partial<OutlookIssueItem> & { nextAction?: string; status?: string }
): OutlookIssueItem => ({
  id: item.id || makeId('issue'),
  issue: item.issue || '',
  improvementMethod: item.improvementMethod || item.nextAction || '',
  owner: item.owner || '',
  carryForward: item.carryForward ?? true,
  status: normalizeIssueStatus(item.status),
  source: item.source,
});

const normalizeReportGroups = (record: Partial<OutlookMeetingRecord>): OutlookReportGroup[] => {
  if (Array.isArray(record.reportGroups) && record.reportGroups.length > 0) {
    return record.reportGroups
      .map((group, index) => ({
        id: group.id || makeId('group'),
        name: group.name || `分組 ${index + 1}`,
        order: Number.isFinite(group.order) ? group.order : index + 1,
        isActive: true,
        currentSummary: group.currentSummary || '',
        items: Array.isArray(group.items) && group.items.length > 0
          ? group.items.map(item => normalizeIssueItem(item))
          : [makeDefaultIssueItem()],
      }))
      .sort((a, b) => a.order - b.order)
      .map((group, index) => ({ ...group, order: index + 1 }));
  }

  const groups = makeDefaultReportGroups();
  if (record.groupReports) {
    groups[0] = { ...groups[0], currentSummary: record.groupReports };
  }
  return groups;
};

const normalizeMeetingFeedbacks = (record: Partial<OutlookMeetingRecord>): OutlookMeetingFeedback[] => {
  if (Array.isArray(record.meetingFeedbacks) && record.meetingFeedbacks.length > 0) {
    const normalized = record.meetingFeedbacks
      .map((feedback, index) => ({
        id: feedback.id || makeId('feedback'),
        category: normalizeFeedbackCategory(feedback.category, DEFAULT_MEETING_FEEDBACKS[index] || `回饋 ${index + 1}`),
        speaker: feedback.speaker || '',
        summary: feedback.summary || '',
        followUp: '',
        owner: '',
        order: Number.isFinite(feedback.order) ? feedback.order : index + 1,
      }))
      .filter(feedback =>
        DEFAULT_MEETING_FEEDBACKS.includes(feedback.category) ||
        feedback.speaker.trim() ||
        feedback.summary.trim()
      );

    return (normalized.length > 0 ? normalized : makeDefaultMeetingFeedbacks())
      .sort((a, b) => a.order - b.order)
      .map((feedback, index) => ({ ...feedback, order: index + 1 }));
  }
  return makeDefaultMeetingFeedbacks();
};

const normalizeRecord = (record: OutlookMeetingRecord, event: EventOption, year: number): OutlookMeetingRecord => {
  const base = makeEmptyRecord(event, year);
  return {
    ...base,
    ...record,
    publicSummary: record.publicSummary || '',
    groupReports: record.groupReports || '',
    reportGroups: normalizeReportGroups(record),
    meetingFeedbacks: normalizeMeetingFeedbacks(record),
    gospelFollowUp: '',
    gratitudeNotes: '',
    genderRatio: record.genderRatio || '',
    ageRange: record.ageRange || '',
    faithDistribution: record.faithDistribution || '',
    referralSourceDistribution: record.referralSourceDistribution || '',
    actionItems: Array.isArray(record.actionItems) ? record.actionItems : [],
    statsSheetSource: undefined,
    statsSync: undefined,
    syncedStats: undefined,
  };
};

const cleanRecord = (record: OutlookMeetingRecord, status: OutlookRecordStatus): OutlookMeetingRecord => ({
  ...record,
  status,
  title: cleanText(record.title),
  meetingDateTime: cleanText(record.meetingDateTime),
  meetingLocation: cleanText(record.meetingLocation),
  recorderName: cleanText(record.recorderName),
  recorderEmails: (record.recorderEmails || [])
    .map(email => cleanText(email).toLowerCase())
    .filter(Boolean)
    .filter((email, index, all) => all.indexOf(email) === index),
  infoTeamName: cleanText(record.infoTeamName),
  publicSummary: cleanText(record.publicSummary),
  groupReports: cleanText(record.groupReports),
  gospelFollowUp: '',
  gratitudeNotes: '',
  genderRatio: cleanText(record.genderRatio),
  ageRange: cleanText(record.ageRange),
  faithDistribution: cleanText(record.faithDistribution),
  referralSourceDistribution: cleanText(record.referralSourceDistribution),
  dayStats: record.dayStats.map(day => ({
    ...day,
    label: cleanText(day.label),
    patientCount: day.patientCount,
    visitCount: day.visitCount,
  })),
  departmentStats: record.departmentStats.map(dept => ({
    ...dept,
    name: cleanText(dept.name),
    visitCount: dept.visitCount,
  })),
  reportGroups: record.reportGroups.map(group => ({
    id: group.id,
    name: cleanText(group.name),
    order: group.order,
    isActive: true,
    currentSummary: cleanText(group.currentSummary),
    items: group.items.map(item => ({
      id: item.id,
      issue: cleanText(item.issue),
      improvementMethod: cleanText(item.improvementMethod),
      owner: cleanText(item.owner),
      carryForward: item.carryForward,
      status: item.status,
      source: item.source,
    })),
  })),
  meetingFeedbacks: (record.meetingFeedbacks || []).map(feedback => ({
    ...feedback,
    category: cleanText(feedback.category),
    speaker: cleanText(feedback.speaker),
    summary: cleanText(feedback.summary),
    followUp: '',
    owner: '',
  })),
  actionItems: record.actionItems || [],
  statsSheetSource: undefined,
  statsSync: undefined,
  syncedStats: undefined,
});

const textInputClass = 'w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100';
const numberInputClass = 'w-full rounded-lg border-2 border-gray-200 px-3 py-2 text-right font-black text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100';
const compactTextareaClass = 'w-full min-h-20 rounded-lg border-2 border-gray-200 px-3 py-2 leading-6 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100';

interface OutlookRecordsProps {
  events: EventOption[];
  systemYear: number;
  showToast: (type: ToastType, title: string, message?: string) => void;
}

const OutlookRecords: React.FC<OutlookRecordsProps> = ({ events, systemYear, showToast }) => {
  const [records, setRecords] = useState<OutlookMeetingRecord[]>([]);
  const [selectedEventId, setSelectedEventId] = useState(events[0]?.id || '');
  const [form, setForm] = useState<OutlookMeetingRecord | null>(null);
  const [activeGroupId, setActiveGroupId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);

  useEffect(() => {
    if (events.length > 0 && !selectedEventId) {
      setSelectedEventId(events[0].id);
    }
  }, [events, selectedEventId]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    getOutlookRecords(systemYear)
      .then(data => {
        if (mounted) setRecords(data);
      })
      .catch(error => {
        console.error(error);
        showToast('error', '讀取失敗', '無法載入展望會紀錄');
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [systemYear]);

  useEffect(() => {
    const selectedEvent = events.find(event => event.id === selectedEventId);
    if (!selectedEvent) {
      setForm(null);
      return;
    }

    const existing = records.find(record => record.eventId === selectedEvent.id);
    setForm(normalizeRecord(existing || makeEmptyRecord(selectedEvent, systemYear), selectedEvent, systemYear));
  }, [events, records, selectedEventId, systemYear]);

  useEffect(() => {
    if (!form?.reportGroups.length) {
      setActiveGroupId('');
      return;
    }

    const stillExists = form.reportGroups.some(group => group.id === activeGroupId);
    if (stillExists) return;

    const westernMedicineGroup = form.reportGroups.find(group => group.name === '西醫');
    setActiveGroupId((westernMedicineGroup || form.reportGroups[0]).id);
  }, [activeGroupId, form]);

  const totals = useMemo(() => {
    const patientTotal = form?.dayStats.reduce((sum, day) => sum + day.patientCount, 0) || 0;
    const visitTotal = form?.dayStats.reduce((sum, day) => sum + day.visitCount, 0) || 0;
    const departmentVisitTotal = form?.departmentStats.reduce((sum, dept) => sum + dept.visitCount, 0) || 0;
    return { patientTotal, visitTotal, departmentVisitTotal };
  }, [form]);

  const completion = useMemo(() => {
    if (!form) return { textReady: false, statsReady: false, actionReady: false };
    const hasGroupContent = form.reportGroups.some(group =>
      group.currentSummary.trim() ||
      group.items.some(item => item.issue.trim() && item.improvementMethod.trim())
    );
    const hasFeedbackContent = (form.meetingFeedbacks || []).some(feedback =>
      feedback.category.trim() && feedback.speaker.trim() && feedback.summary.trim()
    );
    const textReady = Boolean(
      form.title &&
      form.meetingDateTime &&
      form.meetingLocation &&
      form.recorderName &&
      hasGroupContent &&
      hasFeedbackContent
    );
    const statsReady = totals.patientTotal > 0 && totals.visitTotal > 0;
    const actionReady = form.reportGroups.some(group =>
      group.items.some(item => item.issue.trim() && item.improvementMethod.trim())
    );
    return { textReady, statsReady, actionReady };
  }, [form, totals.patientTotal, totals.visitTotal]);

  const sortedReportGroups = useMemo(
    () => form?.reportGroups.slice().sort((a, b) => a.order - b.order) || [],
    [form?.reportGroups]
  );

  const activeReportGroup = useMemo(
    () => sortedReportGroups.find(group => group.id === activeGroupId) || sortedReportGroups[0],
    [activeGroupId, sortedReportGroups]
  );

  const activeGroupIndex = activeReportGroup
    ? sortedReportGroups.findIndex(group => group.id === activeReportGroup.id)
    : -1;

  const updateForm = (patch: Partial<OutlookMeetingRecord>) => {
    setForm(prev => prev ? { ...prev, ...patch } : prev);
  };

  const applyRecord = (record: OutlookMeetingRecord) => {
    setRecords(prev => {
      const exists = prev.some(existing => existing.id === record.id);
      return exists
        ? prev.map(existing => existing.id === record.id ? record : existing)
        : [...prev, record];
    });
    setForm(record);
  };

  const updateReportGroup = (id: string, patch: Partial<OutlookReportGroup>) => {
    updateForm({
      reportGroups: form?.reportGroups.map(group => group.id === id ? { ...group, ...patch } : group) || [],
    });
  };

  const updateIssueItem = (groupId: string, itemId: string, patch: Partial<OutlookIssueItem>) => {
    updateForm({
      reportGroups: form?.reportGroups.map(group => group.id === groupId
        ? {
          ...group,
          items: group.items.map(item => item.id === itemId ? { ...item, ...patch } : item),
        }
        : group) || [],
    });
  };

  const updateMeetingFeedback = (id: string, patch: Partial<OutlookMeetingFeedback>) => {
    updateForm({
      meetingFeedbacks: (form?.meetingFeedbacks || []).map(feedback =>
        feedback.id === id ? { ...feedback, ...patch } : feedback
      ),
    });
  };

  const addMeetingFeedback = () => {
    const nextFeedback: OutlookMeetingFeedback = {
      id: makeId('feedback'),
      category: '',
      speaker: '',
      summary: '',
      followUp: '',
      owner: '',
      order: (form?.meetingFeedbacks?.length || 0) + 1,
    };
    updateForm({
      meetingFeedbacks: [...(form?.meetingFeedbacks || []), nextFeedback],
    });
  };

  const removeMeetingFeedback = (id: string) => {
    updateForm({
      meetingFeedbacks: (form?.meetingFeedbacks || [])
        .filter(feedback => feedback.id !== id)
        .map((feedback, index) => ({ ...feedback, order: index + 1 })),
    });
  };

  const updateDayStat = (id: string, patch: Partial<OutlookMeetingRecord['dayStats'][number]>) => {
    updateForm({
      dayStats: form?.dayStats.map(day => day.id === id ? { ...day, ...patch } : day) || [],
    });
  };

  const addDayStat = () => {
    updateForm({
      dayStats: [
        ...(form?.dayStats || []),
        {
          id: makeId('day'),
          label: `DAY${(form?.dayStats.length || 0) + 1}`,
          patientCount: 0,
          visitCount: 0,
        },
      ],
    });
  };

  const removeDayStat = (id: string) => {
    updateForm({
      dayStats: (form?.dayStats || []).filter(day => day.id !== id),
    });
  };

  const updateDepartmentStat = (id: string, patch: Partial<OutlookMeetingRecord['departmentStats'][number]>) => {
    updateForm({
      departmentStats: form?.departmentStats.map(dept => dept.id === id ? { ...dept, ...patch } : dept) || [],
    });
  };

  const addDepartmentStat = () => {
    updateForm({
      departmentStats: [
        ...(form?.departmentStats || []),
        {
          id: makeId('dept'),
          name: '',
          visitCount: 0,
        },
      ],
    });
  };

  const removeDepartmentStat = (id: string) => {
    updateForm({
      departmentStats: (form?.departmentStats || []).filter(dept => dept.id !== id),
    });
  };

  const addReportGroup = () => {
    const nextGroup: OutlookReportGroup = {
      id: makeId('group'),
      name: '',
      order: (form?.reportGroups.length || 0) + 1,
      isActive: true,
      currentSummary: '',
      items: [makeDefaultIssueItem()],
    };
    updateForm({
      reportGroups: [
        ...(form?.reportGroups || []),
        nextGroup,
      ],
    });
    setActiveGroupId(nextGroup.id);
  };

  const moveReportGroup = (id: string, direction: -1 | 1) => {
    if (!form) return;
    const groups = [...form.reportGroups].sort((a, b) => a.order - b.order);
    const currentIndex = groups.findIndex(group => group.id === id);
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= groups.length) return;
    [groups[currentIndex], groups[nextIndex]] = [groups[nextIndex], groups[currentIndex]];
    updateForm({
      reportGroups: groups.map((group, index) => ({ ...group, order: index + 1 })),
    });
  };

  const addIssueItem = (groupId: string) => {
    updateForm({
      reportGroups: form?.reportGroups.map(group => group.id === groupId
        ? { ...group, items: [...group.items, makeDefaultIssueItem()] }
        : group) || [],
    });
  };

  const removeIssueItem = (groupId: string, itemId: string) => {
    updateForm({
      reportGroups: form?.reportGroups.map(group => group.id === groupId
        ? { ...group, items: group.items.filter(item => item.id !== itemId) }
        : group) || [],
    });
  };

  const saveRecord = async (status: OutlookRecordStatus): Promise<OutlookMeetingRecord | null> => {
    if (!form) return null;

    if (status !== 'draft' && (!completion.textReady || !completion.statsReady)) {
      showToast('info', '尚未完成', '請先填寫公開紀錄基本內容與服務統計');
      return null;
    }

    const nextRecord = cleanRecord(form, status);
    setIsSaving(true);
    try {
      await saveOutlookRecord(nextRecord);
      applyRecord(nextRecord);
      showToast(
        'success',
        status === 'draft' ? '草稿已儲存' : status === 'review' ? '已送出審核' : '已標記發布',
        nextRecord.title
      );
      return nextRecord;
    } catch (error) {
      console.error(error);
      showToast('error', '儲存失敗', '展望會紀錄無法寫入資料庫');
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const copyRecorderLink = async () => {
    if (!form) return;
    if (!form.recorderEmails?.length) {
      showToast('info', '尚未指定紀錄者', '請先填寫紀錄者 Google Email。');
      return;
    }

    setIsCopyingLink(true);
    try {
      const saved = await saveRecord('draft');
      if (!saved) return;

      const link = `${window.location.origin}/outlook-records/submit/${encodeURIComponent(saved.id)}`;
      await navigator.clipboard.writeText(link);
      showToast('success', '連結已複製', '已儲存草稿並複製紀錄者填寫連結。');
    } catch (error) {
      console.error(error);
      showToast('error', '複製失敗', '請確認瀏覽器允許剪貼簿權限。');
    } finally {
      setIsCopyingLink(false);
    }
  };

  if (events.length === 0) {
    return (
      <div className="p-8 bg-white rounded-2xl border-2 border-dashed text-center text-gray-500">
        <ClipboardList size={48} className="mx-auto mb-3 text-gray-300" />
        <p className="text-xl font-bold mb-2">目前 {systemYear} 年度尚未建立任何梯隊</p>
        <p>請先在「梯隊設定」新增梯隊。</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in-up">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <ClipboardList className="text-indigo-600" size={28} />
              <h2 className="text-2xl font-bold text-gray-900">展望會紀錄</h2>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:min-w-[520px]">
            <select
              value={selectedEventId}
              onChange={(event) => setSelectedEventId(event.target.value)}
              className="flex-1 rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-3 font-bold text-gray-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
            >
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.dateRange} - {event.location}
                </option>
              ))}
            </select>
            {form && (
              <span className={`inline-flex items-center justify-center rounded-xl border px-4 py-3 text-sm font-bold ${STATUS_CLASS[form.status]}`}>
                {STATUS_LABEL[form.status]}
              </span>
            )}
          </div>
        </div>
      </div>

      {isLoading || !form ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-500">載入中...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <p className="text-sm font-bold text-gray-500 mb-2">看診人數</p>
              <p className="text-4xl font-black text-gray-900">{totals.patientTotal}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <p className="text-sm font-bold text-gray-500 mb-2">診次合計</p>
              <p className="text-4xl font-black text-indigo-700">{totals.visitTotal}</p>
            </div>
            <div className={`rounded-2xl border p-5 ${totals.departmentVisitTotal === totals.visitTotal ? 'bg-white border-gray-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className="text-sm font-bold text-gray-500 mb-2">各科診次加總</p>
              <p className="text-4xl font-black text-gray-900">{totals.departmentVisitTotal}</p>
              {totals.departmentVisitTotal !== totals.visitTotal && (
                <p className="mt-2 text-xs font-bold text-amber-700 flex items-center gap-1">
                  <AlertTriangle size={14} /> 與診次合計不一致
                </p>
              )}
            </div>
          </div>

          <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
            <div className="flex items-center gap-3 mb-5 border-b pb-4">
              <FileText className="text-indigo-600" size={24} />
              <h3 className="text-xl font-bold text-gray-900">紀錄人員填寫</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <label>
                <span className="block text-sm font-bold text-gray-700 mb-1">公開標題</span>
                <input className={textInputClass} value={form.title} onChange={e => updateForm({ title: e.target.value })} />
              </label>
              <label>
                <span className="block text-sm font-bold text-gray-700 mb-1">會議日期時間</span>
                <input className={textInputClass} placeholder="例：114/09/21 12:30" value={form.meetingDateTime} onChange={e => updateForm({ meetingDateTime: e.target.value })} />
              </label>
              <label>
                <span className="block text-sm font-bold text-gray-700 mb-1">會議地點</span>
                <input className={textInputClass} value={form.meetingLocation} onChange={e => updateForm({ meetingLocation: e.target.value })} />
              </label>
              <label>
                <span className="block text-sm font-bold text-gray-700 mb-1">紀錄者</span>
                <input className={textInputClass} value={form.recorderName} onChange={e => updateForm({ recorderName: e.target.value })} />
              </label>
            </div>

            <div className="mb-4 rounded-xl border border-indigo-100 bg-indigo-50 p-4">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
                <label>
                  <span className="block text-sm font-bold text-indigo-900 mb-1">紀錄者 Google Email</span>
                  <textarea
                    className="w-full min-h-20 rounded-lg border-2 border-indigo-100 bg-white px-3 py-2 text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    value={(form.recorderEmails || []).join('\n')}
                    onChange={e => updateForm({ recorderEmails: normalizeEmailList(e.target.value) })}
                    placeholder="每行一個 Email，例如 recorder@gmail.com"
                  />
                </label>
                <div className="flex flex-col justify-end gap-2">
                  <button
                    type="button"
                    onClick={copyRecorderLink}
                    disabled={isSaving || isCopyingLink}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <Copy size={18} /> 複製填寫連結
                  </button>
                  <p className="max-w-sm text-xs font-bold leading-5 text-indigo-800">
                    系統會先儲存草稿。紀錄者開啟連結後仍必須用指定 Google 帳號登入。
                  </p>
                </div>
              </div>
            </div>

          </section>

          <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5 border-b pb-4">
              <div className="flex items-center gap-3">
                <ClipboardList className="text-indigo-600" size={24} />
                <h3 className="text-xl font-bold text-gray-900">分組報告與改善追蹤</h3>
              </div>
              <button
                type="button"
                onClick={addReportGroup}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white hover:bg-indigo-700"
              >
                <Plus size={16} /> 新增分組
              </button>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[260px_1fr] gap-4">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="mb-2 flex items-center justify-between text-xs font-bold text-gray-500">
                  <span>選擇分組</span>
                  <span>{sortedReportGroups.length} 組</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-1 gap-2">
                  {sortedReportGroups.map(group => {
                    const filledItems = group.items.filter(item => item.issue.trim() || item.improvementMethod.trim()).length;
                    const isCurrent = group.id === activeReportGroup?.id;
                    return (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => setActiveGroupId(group.id)}
                        className={`flex min-h-12 items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                          isCurrent
                            ? 'border-indigo-500 bg-white text-indigo-800 shadow-sm'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-200 hover:bg-indigo-50'
                        }`}
                      >
                        <span className="min-w-0 truncate text-sm font-bold">{group.name || '未命名分組'}</span>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-black ${filledItems > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {filledItems}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {activeReportGroup && (
                <div className="rounded-xl border border-gray-200 bg-white p-3 md:p-4">
                  <div className="mb-3 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
                    <label>
                      <span className="block text-xs font-bold text-gray-500 mb-1">目前填寫分組</span>
                      <input
                        className={textInputClass}
                        value={activeReportGroup.name}
                        onChange={e => updateReportGroup(activeReportGroup.id, { name: e.target.value })}
                        placeholder="例：西醫"
                      />
                    </label>
                    <div className="flex flex-wrap items-end gap-2">
                      <button
                        type="button"
                        onClick={() => moveReportGroup(activeReportGroup.id, -1)}
                        disabled={activeGroupIndex <= 0}
                        className="h-11 w-11 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                        title="上移"
                      >
                        <ChevronUp size={18} className="mx-auto" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveReportGroup(activeReportGroup.id, 1)}
                        disabled={activeGroupIndex < 0 || activeGroupIndex === sortedReportGroups.length - 1}
                        className="h-11 w-11 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                        title="下移"
                      >
                        <ChevronDown size={18} className="mx-auto" />
                      </button>
                    </div>
                  </div>

                  <div className="mb-3 rounded-xl border border-gray-200 bg-white p-3">
                    <label className="block">
                      <span className="block text-xs font-bold text-gray-500 mb-1">本組執行亮點與可延續做法</span>
                      <textarea
                        className={compactTextareaClass}
                        value={activeReportGroup.currentSummary}
                        onChange={e => updateReportGroup(activeReportGroup.id, { currentSummary: e.target.value })}
                        placeholder="例：掛號分流清楚、叫號動線順暢、同工交接即時，可作為下次流程參考。"
                      />
                    </label>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <h4 className="font-bold text-gray-900">需要改善的事項</h4>
                      <button
                        type="button"
                        onClick={() => addIssueItem(activeReportGroup.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 px-3 py-1.5 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                      >
                        <Plus size={15} /> 新增事項
                      </button>
                    </div>

                    {activeReportGroup.items.map((item, itemIndex) => (
                      <div key={item.id} className="rounded-xl border border-gray-200 bg-white p-3">
                        {item.source && (
                          <p className="mb-2 rounded-lg bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700">
                            延續自：{item.source.eventLabel} / {item.source.groupName}
                          </p>
                        )}

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                          <label>
                            <span className="block text-xs font-bold text-gray-500 mb-1">問題</span>
                            <textarea
                              className={compactTextareaClass}
                              value={item.issue}
                              onChange={e => updateIssueItem(activeReportGroup.id, item.id, { issue: e.target.value })}
                              placeholder="具體問題、觀察或需求"
                            />
                          </label>
                          <label>
                            <span className="block text-xs font-bold text-gray-500 mb-1">下次建議</span>
                            <textarea
                              className={compactTextareaClass}
                              value={item.improvementMethod}
                              onChange={e => updateIssueItem(activeReportGroup.id, item.id, { improvementMethod: e.target.value })}
                              placeholder="下次怎麼做、誰先準備"
                            />
                          </label>
                        </div>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_150px_190px_auto] gap-2 items-end">
                          <label>
                            <span className="block text-xs font-bold text-gray-500 mb-1">負責/追蹤單位</span>
                            <input
                              className={textInputClass}
                              value={item.owner}
                              onChange={e => updateIssueItem(activeReportGroup.id, item.id, { owner: e.target.value })}
                              placeholder="例：西醫組"
                            />
                          </label>
                          <label>
                            <span className="block text-xs font-bold text-gray-500 mb-1">狀態</span>
                            <select
                              className={textInputClass}
                              value={item.status}
                              onChange={e => updateIssueItem(activeReportGroup.id, item.id, { status: e.target.value as OutlookIssueItem['status'] })}
                            >
                              <option value="open">待追蹤</option>
                              <option value="resolved">已處理</option>
                              <option value="dropped">不續追</option>
                            </select>
                          </label>
                          <label className="inline-flex h-11 items-center gap-2 rounded-lg bg-white px-3 text-sm font-bold text-gray-700">
                            <input
                              type="checkbox"
                              checked={item.carryForward}
                              onChange={e => updateIssueItem(activeReportGroup.id, item.id, { carryForward: e.target.checked })}
                              className="h-4 w-4 accent-indigo-600"
                            />
                            下次追蹤
                          </label>
                          <button
                            type="button"
                            onClick={() => removeIssueItem(activeReportGroup.id, item.id)}
                            disabled={activeReportGroup.items.length === 1 && itemIndex === 0}
                            className="inline-flex h-11 items-center justify-center gap-1 rounded-lg border border-red-100 px-3 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-40"
                          >
                            <Trash2 size={16} /> 刪除
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-5 border-b pb-4">
              <div className="flex items-center gap-3">
                <FileText className="text-indigo-600" size={24} />
              <h3 className="text-xl font-bold text-gray-900">整體回饋</h3>
              </div>
              <button
                type="button"
                onClick={addMeetingFeedback}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white hover:bg-indigo-700"
              >
                <Plus size={16} /> 新增發言
              </button>
            </div>

            <div className="space-y-3">
              {(form.meetingFeedbacks || []).map(feedback => (
                <div key={feedback.id} className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                    <label>
                      <span className="block text-xs font-bold text-gray-500 mb-1">類別</span>
                      <input
                        className={textInputClass}
                        value={feedback.category}
                        onChange={e => updateMeetingFeedback(feedback.id, { category: e.target.value })}
                        placeholder="例：地方教會"
                      />
                    </label>
                    <label>
                      <span className="block text-xs font-bold text-gray-500 mb-1">發言單位/人員</span>
                      <input
                        className={textInputClass}
                        value={feedback.speaker}
                        onChange={e => updateMeetingFeedback(feedback.id, { speaker: e.target.value })}
                        placeholder="例：高執事"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => removeMeetingFeedback(feedback.id)}
                      className="inline-flex h-11 items-center justify-center gap-1 rounded-lg border border-red-100 px-3 text-sm font-bold text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={16} /> 刪除
                    </button>
                  </div>
                  <label className="mt-3 block">
                    <span className="block text-xs font-bold text-gray-500 mb-1">重點摘要</span>
                    <textarea
                      className={compactTextareaClass}
                      value={feedback.summary}
                      onChange={e => updateMeetingFeedback(feedback.id, { summary: e.target.value })}
                    />
                  </label>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-6">
            <div className="flex items-center gap-3 mb-5 border-b pb-4">
              <BarChart3 className="text-indigo-600" size={24} />
              <h3 className="text-xl font-bold text-gray-900">資訊組統計</h3>
            </div>

            <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <label>
                  <span className="block text-sm font-bold text-gray-700 mb-1">資訊組填寫人</span>
                  <input className={textInputClass} value={form.infoTeamName} onChange={e => updateForm({ infoTeamName: e.target.value })} />
                </label>
                <label>
                  <span className="block text-sm font-bold text-gray-700 mb-1">男女比</span>
                  <input
                    className={textInputClass}
                    value={form.genderRatio}
                    onChange={e => updateForm({ genderRatio: e.target.value })}
                    placeholder="例：男 46 / 女 128"
                  />
                </label>
                <label>
                  <span className="block text-sm font-bold text-gray-700 mb-1">年齡摘要</span>
                  <input
                    className={textInputClass}
                    value={form.ageRange}
                    onChange={e => updateForm({ ageRange: e.target.value })}
                    placeholder="例：最多 60-70 歲"
                  />
                </label>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <label>
                  <span className="block text-sm font-bold text-gray-700 mb-1">信仰分布</span>
                  <textarea
                    className={compactTextareaClass}
                    value={form.faithDistribution}
                    onChange={e => updateForm({ faithDistribution: e.target.value })}
                    placeholder="例：主內同靈 90、民間信仰 29、基督教 4"
                  />
                </label>
                <label>
                  <span className="block text-sm font-bold text-gray-700 mb-1">如何得知義診</span>
                  <textarea
                    className={compactTextareaClass}
                    value={form.referralSourceDistribution}
                    onChange={e => updateForm({ referralSourceDistribution: e.target.value })}
                    placeholder="例：LINE 8、親友推薦 16、其他 15"
                  />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-bold text-gray-900">每日人次與診次</h4>
                  <button
                    type="button"
                    onClick={addDayStat}
                    className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                  >
                    <Plus size={15} /> 新增場次
                  </button>
                </div>
                <div className="space-y-3 p-4">
                  {form.dayStats.map(day => (
                    <div key={day.id} className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white p-3 md:grid-cols-[1fr_120px_120px_auto] md:items-end">
                      <label>
                        <span className="block text-xs font-bold text-gray-500 mb-1">場次</span>
                        <input
                          className={textInputClass}
                          value={day.label}
                          onChange={e => updateDayStat(day.id, { label: e.target.value })}
                          placeholder="例：DAY1"
                        />
                      </label>
                      <label>
                        <span className="block text-xs font-bold text-gray-500 mb-1">人數</span>
                        <input
                          type="number"
                          min={0}
                          className={numberInputClass}
                          value={day.patientCount}
                          onChange={e => updateDayStat(day.id, { patientCount: parseStatCount(e.target.value) })}
                        />
                      </label>
                      <label>
                        <span className="block text-xs font-bold text-gray-500 mb-1">診次</span>
                        <input
                          type="number"
                          min={0}
                          className={numberInputClass}
                          value={day.visitCount}
                          onChange={e => updateDayStat(day.id, { visitCount: parseStatCount(e.target.value) })}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeDayStat(day.id)}
                        disabled={form.dayStats.length <= 1}
                        className="inline-flex h-11 items-center justify-center gap-1 rounded-lg border border-red-100 px-3 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-40"
                      >
                        <Trash2 size={16} /> 刪除
                      </button>
                    </div>
                  ))}
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm font-bold text-gray-700">
                    合計：看診人數 {formatCount(totals.patientTotal)}，診次 {formatCount(totals.visitTotal)}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-bold text-gray-900">各科診次</h4>
                  <button
                    type="button"
                    onClick={addDepartmentStat}
                    className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                  >
                    <Plus size={15} /> 新增科別
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2">
                  {form.departmentStats.map(dept => (
                    <div key={dept.id} className="grid grid-cols-1 gap-2 rounded-xl border border-gray-200 bg-white p-3 sm:grid-cols-[1fr_96px_auto] sm:items-end">
                      <label>
                        <span className="block text-xs font-bold text-gray-500 mb-1">科別</span>
                        <input
                          className={textInputClass}
                          value={dept.name}
                          onChange={e => updateDepartmentStat(dept.id, { name: e.target.value })}
                          placeholder="例：內科"
                        />
                      </label>
                      <label>
                        <span className="block text-xs font-bold text-gray-500 mb-1">診次</span>
                        <input
                          type="number"
                          min={0}
                          className={numberInputClass}
                          value={dept.visitCount}
                          onChange={e => updateDepartmentStat(dept.id, { visitCount: parseStatCount(e.target.value) })}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removeDepartmentStat(dept.id)}
                        disabled={form.departmentStats.length <= 1}
                        className="inline-flex h-11 w-full items-center justify-center rounded-lg border border-red-100 text-red-600 hover:bg-red-50 disabled:opacity-40 sm:w-11"
                        title="刪除"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm font-bold">
              <span className={completion.textReady ? 'text-emerald-700' : 'text-gray-400'}>公開紀錄</span>
              <span className={completion.statsReady ? 'text-emerald-700' : 'text-gray-400'}>服務統計</span>
              <span className={completion.actionReady ? 'text-emerald-700' : 'text-gray-400'}>改善項目</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => saveRecord('draft')}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-gray-200 px-5 py-3 font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Save size={18} /> 儲存草稿
              </button>
              <button
                type="button"
                onClick={() => saveRecord('review')}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-5 py-3 font-bold text-white hover:bg-amber-600 disabled:opacity-50"
              >
                <Send size={18} /> 送出審核
              </button>
              <button
                type="button"
                onClick={() => saveRecord('published')}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <UploadCloud size={18} /> 標記發布
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(OutlookRecords);
